// Pure completion calculation logic — no Zustand dependency.
// Shared between frontend (store.ts) and backend (presalesDashboard worker).

import type { ScopeData, TriState } from '../types'

export const normalizeTriState = (value: unknown): TriState => {
  if (value === 'yes' || value === 'no' || value === 'unknown') return value
  if (value === true) return 'yes'
  if (value === false) return 'no'
  return 'unknown'
}

export const isYes = (value: unknown) => normalizeTriState(value) === 'yes'
export const isUnknown = (value: unknown) => normalizeTriState(value) === 'unknown'

export const hasAnyNonUnknown = (obj: Record<string, unknown>) => Object.values(obj).some((v) => !isUnknown(v))

// Helper to calculate tab completion percentage
// When scope is an upsell, 'unknown' values represent "Existing Settings" and count as complete.
export const calculateTabCompletion = (scope: ScopeData) => {
  const completions: Record<string, number> = {}
  const upsell = scope.generalInfo.isUpsell || false

  // For upsells, 'unknown' means "Existing Settings" — treat as a valid choice
  const isDecided = (value: unknown) => upsell || !isUnknown(value)

  // General Info - check fields visible on the General tab
  // parentChild and resellerAgreement live on the Advanced tab, not here
  const gi = scope.generalInfo
  const generalFields = [
    gi.clientUseCase.trim() !== '',
    gi.productionAccessDate !== '',
    gi.goLiveDate !== '',
    gi.integrationType !== 'unknown',
    isDecided(gi.ssoAccess),
    isDecided(gi.aiFeatures),
    gi.phases.length > 0 && gi.phases[0].trim() !== '' && gi.phases.slice(1).every((p) => p.trim() !== ''),
  ]
  completions.general = Math.round((generalFields.filter(Boolean).length / generalFields.length) * 100)

  // KYC - if explicitly set to No, it's fully resolved (100%); if unknown, 0% (or 100% for upsells)
  const kyc = scope.kycConfig
  if (normalizeTriState(kyc.kycRequired) === 'no') {
    completions.kyc = 100 // Explicitly set to No — KYC not needed
  } else if (normalizeTriState(kyc.kycRequired) === 'unknown') {
    completions.kyc = upsell ? 100 : 0 // Upsell: "Existing Settings" is valid
  } else {
    const hasMeaningfulJurisdiction = kyc.primaryCustomerJurisdictions.some(
      (j) => !["Don't know", 'Unknown / To Be Determined'].includes(j)
    )
    const includesAustralia = kyc.primaryCustomerJurisdictions.includes('Australia')
    const hasInternationalJurisdiction = kyc.primaryCustomerJurisdictions.some(
      (j) => !['Australia', 'New Zealand', 'Unknown / To Be Determined', "Don't know"].includes(j)
    )

    const isReportingEntityValue = normalizeTriState(kyc.isReportingEntity)
    const creditHeaderMode = (kyc as any).enableConsentSources
    const creditHeaderEnabled = creditHeaderMode === 'yes'

    const hasRuleset = kyc.rulesets && kyc.rulesets.length > 0 && kyc.rulesets.some((r) => r.rulesetKey && r.rulesetKey !== 'unknown')

    const kycRequirements: Array<{ applicable: boolean; done: boolean }> = [
      { applicable: true, done: true }, // KYC required decision made — counts as progress
      { applicable: true, done: hasRuleset },
      { applicable: true, done: hasMeaningfulJurisdiction },
      // isReportingEntity and enableConsentSources live on the Advanced tab, not here
      { applicable: includesAustralia, done: kyc.documentTypesAu.length > 0 || (kyc.auOtherDocumentTypesEnabled && (kyc.auOtherDocumentTypesText || '').trim() !== '') },
      {
        applicable: isYes(isReportingEntityValue) && creditHeaderEnabled,
        done: hasAnyNonUnknown(kyc.creditBureauSources as unknown as Record<string, unknown>),
      },
      {
        applicable: hasInternationalJurisdiction,
        done: hasAnyNonUnknown(kyc.internationalDataSources as unknown as Record<string, unknown>),
      },
    ]

    const applicableKycRequirements = kycRequirements.filter((r) => r.applicable)
    completions.kyc =
      applicableKycRequirements.length === 0
        ? 0
        : Math.round(
            (applicableKycRequirements.filter((r) => r.done).length / applicableKycRequirements.length) * 100
          )
  }

  // AML - check level selected and config
  const aml = scope.amlConfig
  if (!aml.level || aml.level === 'unknown') {
    completions.aml = upsell ? 100 : 0 // Upsell: "Existing Settings" is valid
  } else if (aml.level === 'na') {
    completions.aml = 100 // Explicitly set to N/A
  } else {
    const amlFields = [
      true, // level is already selected (non-empty, non-na)
      aml.vendor !== '' && aml.vendor !== 'unknown',
      aml.monitoringType !== '' && aml.monitoringType !== 'unknown', // Monitoring type decided
      isDecided(aml.screeningEntityTypes.individuals), // Screening individuals decided
      isDecided(aml.screeningEntityTypes.businesses), // Screening businesses decided
      isDecided(aml.pepScreeningConfig.includeFamilyMembers), // PEP family members decided
      isDecided(aml.pepScreeningConfig.includeHistoricalPeps), // PEP historical decided
      // Note: sanctions/adverse media/watchlist sources are NOT required for completion
    ]
    completions.aml = Math.round((amlFields.filter(Boolean).length / amlFields.length) * 100)
  }

  // Biometrics - if explicitly set to No, it's fully resolved (100%)
  const bio = scope.biometricsConfig
  if (normalizeTriState(bio.enabled) === 'no') {
    completions.biometrics = 100 // Explicitly set to Not Required
  } else if (normalizeTriState(bio.enabled) !== 'yes') {
    completions.biometrics = upsell ? 100 : 0 // Upsell: "Existing Settings" is valid
  } else {
    const hasValidFlow =
      Array.isArray(bio.flows) &&
      bio.flows.length > 0 &&
      bio.flows.some(
        (f) =>
          (f as any).idVerificationEnabled === 'yes' ||
          (f as any).idFormEnabled === 'yes' ||
          (f as any).biometricsEnabled === 'yes' ||
          (f as any).reviewScreenEnabled === 'yes' ||
          (f as any).customScreenEnabled === 'yes'
      )
    const hasFlowDocTypes =
      Array.isArray(bio.flows) &&
      bio.flows.length > 0 &&
      bio.flows.some((f) => {
        const docs = Array.isArray((f as any).documentTypes) ? ((f as any).documentTypes as string[]) : []
        if (docs.length === 0) return false
        return docs.some((d) => d !== 'unknown')
      })
    const bioFields = [
      hasValidFlow || (bio.serviceLevel !== '' && bio.serviceLevel !== 'unknown'),
      hasFlowDocTypes || (bio.documentTypes.length > 0 && !bio.documentTypes.includes('unknown')),
      bio.vendor !== '' && bio.vendor !== 'unknown',
    ]
    completions.biometrics = Math.round((bioFields.filter(Boolean).length / bioFields.length) * 100)
  }

  // KYB - if not enabled, show 0% (not configured), if explicitly set to 'no' show 100%
  const kyb = scope.kybConfig
  if (normalizeTriState(kyb.enabled) === 'no') {
    completions.kyb = 100 // Explicitly set to No
  } else if (!kyb.enabled || normalizeTriState(kyb.enabled) === 'unknown') {
    completions.kyb = upsell ? 100 : 0 // Upsell: "Existing Settings" is valid
  } else {
    const kybRequirements: Array<{ applicable: boolean; done: boolean }> = [
      { applicable: true, done: true }, // KYB enabled decision made — counts as progress
      { applicable: true, done: Object.values(kyb.auChecks).some(Boolean) }, // AU checks selected
      { applicable: true, done: isDecided(kyb.uboConsentAware) }, // UBO consent awareness decided
      { applicable: true, done: isDecided(kyb.uboSmsPhoneAware) }, // UBO SMS phone awareness decided
      { applicable: true, done: isDecided(kyb.internationalEnabled) }, // International KYB decided
      { applicable: true, done: isDecided(kyb.trustAnalyserEnabled) }, // Trust analyser decided
      { applicable: true, done: isDecided(kyb.onboardingFormEnabled) }, // Onboarding form decided
      { applicable: true, done: isDecided(kyb.kybAmlOngoingMonitoringEnabled) }, // KYB AML monitoring decided
      { applicable: true, done: isDecided(kyb.continuousKybEnabled) }, // Continuous KYB decided
    ]
    const applicableKybReqs = kybRequirements.filter((r) => r.applicable)
    completions.kyb = applicableKybReqs.length === 0
      ? 0
      : Math.round((applicableKybReqs.filter((r) => r.done).length / applicableKybReqs.length) * 100)
  }

  // Recipes - check at least one recipe configured + core ruleset requirements
  const recipesFields = [scope.workflowsConfig.length > 0]
  completions.recipes = Math.round((recipesFields.filter(Boolean).length / recipesFields.length) * 100)

  // Rules - only tracks fields visible on the Rules tab (not Advanced)
  const rules = scope.rulesConfig
  const rulesRequirements: Array<{ applicable: boolean; done: boolean }> = [
    { applicable: true, done: upsell || rules.fraudEnabled !== 'unknown' },
  ]
  const applicableRulesReqs = rulesRequirements.filter((r) => r.applicable)
  completions.rules = applicableRulesReqs.length === 0
    ? 0
    : Math.round((applicableRulesReqs.filter((r) => r.done).length / applicableRulesReqs.length) * 100)

  // Hours - optional
  completions.hours = scope.scopeHours.length > 0 ? 100 : 0

  // SOW - check if key sections are configured
  const sow = scope.sowConfig
  const sowFields = [
    (sow.deliveryNotes || '').trim() !== '',
    sow.phases.length > 0,
    (sow.raciEntries || []).length > 0,
  ]
  completions.sow = Math.round((sowFields.filter(Boolean).length / sowFields.length) * 100)

  // Actions - percentage of presales actions completed
  const actionsItems = scope.generalInfo.presalesActions || []
  const actionsTotal = 12 // 4 deal actions + 8 presales actions
  const actionsCompleted = actionsItems.filter((a) => a.completed || a.notRequired).length
  completions.actions = Math.round((actionsCompleted / actionsTotal) * 100)

  // Blueprint - weighted completion across sections
  const bp = scope.blueprintConfig
  // Country-level: at least one country with all enabled inputs confirmed
  const hasCompleteCountry = bp.countryRulesets.length > 0 && bp.countryRulesets.some((cr) => {
    if (!cr.country || cr.ruleset === 'unknown') return false
    const enabled = (cr.dataInputs || []).filter((di) => di.enabled)
    return enabled.length > 0 && enabled.every((di) => di.confirmed)
  })
  const blueprintFields = [
    bp.problemHypothesis.trim() !== '',       // Problem hypothesis written
    bp.surfacePain.trim() !== '',             // Surface pain documented
    bp.deepPain.trim() !== '',                // Deep pain documented
    bp.whyNow.trim() !== '',                 // Why now documented
    bp.regulatoryContext.trim() !== '',        // Regulatory & industry context documented
    bp.integrationMethod.trim() !== '' || bp.currentVendors.length > 0, // As-is: integration method or vendor
    bp.currentVendors.length > 0,             // At least one current vendor documented
    bp.countryRulesets.length > 0,            // At least one country ruleset
    hasCompleteCountry,                       // At least one country fully confirmed
    bp.stakeholders.length > 0,              // At least one stakeholder mapped
    bp.prepChecklist.some((c) => c.checked),  // Some prep done
  ]
  completions.blueprint = Math.round((blueprintFields.filter(Boolean).length / blueprintFields.length) * 100)

  // Custom Work & Advanced - no completion tracking
  completions.custom_work = 0
  completions.advanced = 0

  return completions
}
